// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import baseTheme from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { color } from "@rugby/XDL/Utilities";
import * as React from "react";
import styled, { css } from "styled-components";

export type ContainerProps = MobiusStyledComponentProps<
    "div",
    {
        /** CSS value of the background. */
        background?: string;
        /** CSS value of the background. */
        alphaBackgroundColor?: number;
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<ContainerProps>;
        /** The amount of padding inside the Page, in pixels. */
        padding?: number[];
        fullWidth?: boolean;
        fluid?: boolean;
    }
>;

export const StyledContainer = styled.div<
    Pick<ContainerProps, "padding" | "fullWidth" | "background" | "fluid" | "alphaBackgroundColor">
>`
    ${({ padding, theme, fullWidth, background, alphaBackgroundColor, fluid }) => {
        const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
        const containedWidth = breakpointMediaQueries(theme, [
            css`
                max-width: ${maxWidths[1]}px;
                padding-left: ${(padding && padding[1]) || 0}px;
                padding-right: ${(padding && padding[1]) || 0}px;
            `,
            css`
                max-width: ${maxWidths[1]}px;
                padding-left: ${(padding && padding[1]) || 0}px;
                padding-right: ${(padding && padding[1]) || 0}px;
            `,
            css`
                max-width: ${maxWidths[2]}px;
                padding-left: ${(padding && padding[1]) || 0}px;
                padding-right: ${(padding && padding[1]) || 0}px;
            `,
            css`
                max-width: ${maxWidths[3]}px;
                padding-left: ${(padding && padding[1]) || 0}px;
                padding-right: ${(padding && padding[1]) || 0}px;
            `,
            css`
                max-width: ${maxWidths[4]}px;
                padding-left: ${(padding && padding[1]) || 0}px;
                padding-right: ${(padding && padding[1]) || 0}px;
            `,
        ]);
        return css`
            margin: 0 auto;
            background: ${color(background, alphaBackgroundColor || 1)};
            ${fullWidth
                ? `max-width: 100%;& > div {margin: 0 auto;${fluid ? `max-width: 100%;` : containedWidth}}`
                : containedWidth}
            & & {
                max-width: 100%;
                padding: 0;
            }
        `;
    }}

    ${injectCss}
`;
export const Container: React.FC<ContainerProps> = props => <StyledContainer {...props} />;
Container.defaultProps = {
    padding: [15, 15, 15, 15, 15],
};
